/**
 * Client-side image compression — resizes any uploaded image to a sane
 * max dimension and re-encodes as JPEG q0.85 before it ever leaves the
 * browser.
 *
 * Why?
 * ────
 * iPhone / Android cameras emit 4032×3024 JPEGs at 8–14 MB. Uploading
 * that over a 3G connection takes 30–60 s and routinely hits Cloudflare's
 * 30 s ingress ceiling → "Request failed with status code 520".
 * Resizing to 2000 px @ q0.85 drops the file to ~500 KB → upload always
 * completes in 2–5 s, and Claude Vision needs no more detail than that
 * to extract catalogue attributes anyway.
 *
 * Failure mode is graceful — if anything goes wrong (HEIC, exotic codec,
 * canvas blocked) we return the original File so nothing breaks.
 */
const MAX_SIDE = 1024;
const TARGET_QUALITY = 0.7;
const SKIP_BELOW_BYTES = 250 * 1024; // already small? leave it alone
const TARGET_MIME = "image/jpeg";

export async function compressImage(file) {
  if (!file || typeof File === "undefined") return file;
  if (!file.type || !file.type.startsWith("image/")) return file;
  // Don't try to convert HEIC / HEIF in the browser — Safari sometimes
  // handles it via <img>, others don't. Let the backend deal.
  if (/heic|heif/i.test(file.type)) return file;
  // Already small — no win from re-encoding.
  if (file.size <= SKIP_BELOW_BYTES) return file;

  try {
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);
    const { width, height } = fitInside(img.naturalWidth, img.naturalHeight, MAX_SIDE);

    // Already small enough in both dimensions AND already a reasonable JPEG?
    // Skip — re-encoding would just waste CPU.
    if (
      width === img.naturalWidth &&
      height === img.naturalHeight &&
      file.type === TARGET_MIME &&
      file.size <= 2 * 1024 * 1024
    ) {
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // White background for transparent PNGs so Claude sees a solid
    // product photo, not a black silhouette.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, TARGET_MIME, TARGET_QUALITY);
    if (!blob) return file;

    // If compression somehow made the file bigger (rare), keep the original.
    if (blob.size >= file.size) return file;

    const newName = renameToJpeg(file.name || "image.jpg");
    return new File([blob], newName, { type: TARGET_MIME });
  } catch (_e) {
    // Any browser-side failure → fall back to the original file. The
    // backend has a 10 MB cap so worst case we surface a friendly toast.
    return file;
  }
}

/** Run compressImage over a list, preserving order. Used for bulk pickers. */
export async function compressImages(files) {
  const out = [];
  for (const f of files) {
    /* eslint-disable no-await-in-loop */
    out.push(await compressImage(f));
  }
  return out;
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error("read failed"));
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode failed"));
    img.src = src;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), mime, quality);
    } else {
      // toBlob is unavailable on very old Safari → fall back to dataURL.
      const dataUrl = canvas.toDataURL(mime, quality);
      fetch(dataUrl).then((r) => r.blob()).then(resolve).catch(() => resolve(null));
    }
  });
}

function fitInside(w, h, maxSide) {
  if (w <= maxSide && h <= maxSide) return { width: w, height: h };
  if (w >= h) {
    return { width: maxSide, height: Math.round((h * maxSide) / w) };
  }
  return { width: Math.round((w * maxSide) / h), height: maxSide };
}

function renameToJpeg(name) {
  const idx = name.lastIndexOf(".");
  const stem = idx > 0 ? name.slice(0, idx) : name;
  return `${stem}.jpg`;
}
